"use client";

import { useState, useTransition, useEffect } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import ProductSearchBar from "@/components/ProductSearchBar";
import ProductFilters from "@/components/ProductFilters";
import { cn } from "@/lib/utils";

/**
 * ProductsClient handles the interactive filtering of products.
 */
export default function ProductsClient({ 
  categories = [], 
  brands = [], 
  materials = [] 
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const [showFilters, setShowFilters] = useState(false);
  
  // Local state for instant UI feedback
  const [localFilters, setLocalFilters] = useState({
    categories: searchParams.get("categories")?.split(",").filter(Boolean) || [],
    colorCodes: (searchParams.get("colorCodes") || searchParams.get("colors"))?.split(",").filter(Boolean) || [],
    materialIds: searchParams.get("materialIds")?.split(",").filter(Boolean) || [],
    brandIds: searchParams.get("brandIds")?.split(",").filter(Boolean) || [],
    ar: searchParams.get("ar") || "",
    is_smart: searchParams.get("is_smart") || "",
    search: searchParams.get("search") || searchParams.get("q") || searchParams.get("title") || ""
  });

  // Sync local state when URL changes externally (e.g. back button)
  useEffect(() => {
    setLocalFilters({
      categories: searchParams.get("categories")?.split(",").filter(Boolean) || [],
      colorCodes: (searchParams.get("colorCodes") || searchParams.get("colors"))?.split(",").filter(Boolean) || [],
      materialIds: searchParams.get("materialIds")?.split(",").filter(Boolean) || [],
      brandIds: searchParams.get("brandIds")?.split(",").filter(Boolean) || [],
      ar: searchParams.get("ar") || "",
      is_smart: searchParams.get("is_smart") || "",
      search: searchParams.get("search") || searchParams.get("q") || searchParams.get("title") || ""
    });
  }, [searchParams]);

  const handleApply = () => {
    const params = new URLSearchParams(searchParams);
    
    // Reset to page 1 when applying new filters
    params.delete("page");
    // Clean up old search keys if they exist
    params.delete("q");
    params.delete("title");
    // Clean up old filter keys if they exist
    params.delete("brands");
    params.delete("materials");
    params.delete("colors");

    Object.keys(localFilters).forEach(key => {
      const value = localFilters[key];
      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(key, value.join(","));
        } else {
          params.delete(key);
        }
      } else {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
    });

    startTransition(() => {
      router.push(`${pathname}?${params.toString()}`, { scroll: false });
    });
  };

  const handleClear = () => {
    const cleared = {
      categories: [],
      colorCodes: [],
      materialIds: [],
      brandIds: [],
      ar: "",
      is_smart: "",
      search: ""
    };
    setLocalFilters(cleared);
    
    startTransition(() => {
      router.push(pathname, { scroll: false });
    });
  };

  const handleSearchChange = (value) => {
    setLocalFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (newFilters) => {
    setLocalFilters(prev => ({ ...prev, ...newFilters }));
  };

  const selectedCount = (localFilters.categories?.length || 0) +
                       (localFilters.colorCodes?.length || 0) + 
                       (localFilters.materialIds?.length || 0) + 
                       (localFilters.brandIds?.length || 0) +
                       (localFilters.ar ? 1 : 0) +
                       (localFilters.is_smart ? 1 : 0);

  return (
    <div className={`space-y-3 mb-10 transition-opacity duration-300 ${isPending ? 'opacity-70' : 'opacity-100'}`}>
      <ProductSearchBar
        value={localFilters.search}
        onChange={handleSearchChange}
        onSearch={handleApply}
        onToggleFilters={() => setShowFilters(!showFilters)}
        selectedCount={selectedCount}
      />
      
      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        showFilters ? "max-h-[1200px] opacity-100 !mt-0" : "max-h-0 opacity-0 mt-0"
      )}>
        <ProductFilters
          selectedFilters={localFilters}
          onFilterChange={handleFilterChange}
          onApply={handleApply}
          onClear={handleClear}
          categories={categories}
          brands={brands}
          materials={materials}
          isPending={isPending}
        />
      </div>

      {isPending && (
        <div className="text-center text-sm text-berry-500 animate-pulse">
          در حال بروزرسانی لیست محصولات...
        </div>
      )}
    </div>
  );
}
